import React, { useState, useRef, useCallback, useEffect } from "react";
import WorldClockBadge from "@/components/game/WorldClockBadge";
import MissingImagesPanel from "@/components/game/MissingImagesPanel";
import * as storyTTS from "@/utils/storyTTS";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, ChevronRight, ChevronDown, ChevronUp, Loader2, Gift, Swords, AlertTriangle, Crown, Headphones, StopCircle, Send, Sparkles, Footprints } from "lucide-react";
import { toast } from "sonner";
import soundManager from "@/utils/SoundManager";
import ambientMusic from "@/utils/AmbientMusic";
import { t, tf } from "@/utils/i18n";
import { aiErrorMessage } from "@/utils/errorMessages";
import { resolveImageUrl } from "@/utils/imageUrl";
import { sanitizeStoryText } from "@/utils/sanitizeStory";
import DiceRollModal from "@/components/game/DiceRollModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Helper to position speech bubbles dynamically inside panels to prevent overlap.
 */
const getBubblePosition = (dialogueIndex, totalDialogues, hasNarrativeBox) => {
  if (hasNarrativeBox) {
    if (totalDialogues === 1) {
      return "bottom-6 right-6";
    }
    return dialogueIndex === 0 ? "top-14 right-6" : "bottom-6 left-6";
  }
  
  if (totalDialogues === 1) {
    return "top-6 right-6";
  }
  if (totalDialogues === 2) {
    return dialogueIndex === 0 ? "top-6 left-6" : "bottom-6 right-6";
  }
  // 3 or more dialogues
  if (dialogueIndex === 0) return "top-6 left-6";
  if (dialogueIndex === 1) return "top-1/3 right-6";
  return "bottom-6 left-6";
};

/**
 * Parses story text into sequential comic panels (narration boxes and speech bubbles) as fallback.
 */
export function parseStoryToPanels(text, characterName = "") {
  if (!text) return [];
  const paragraphs = text.split("\n").map(p => p.trim()).filter(Boolean);
  const panels = [];
  let bubbleCount = 0;

  paragraphs.forEach((para) => {
    let tempPara = para.replace(/^[\s*\-•]+/, "").trim();
    
    if (tempPara.startsWith("-") || tempPara.startsWith("–") || tempPara.startsWith("—")) {
      const dashParts = tempPara.split(/[-–—]/).map(s => s.trim()).filter(Boolean);
      if (dashParts.length >= 2) {
        tempPara = `"${dashParts[0]}" ${dashParts.slice(1).join(" ")}`;
      } else if (dashParts.length === 1) {
        tempPara = `"${dashParts[0]}"`;
      }
    }

    const quoteRegex = /["“«]([^"”»]+)["”»]/g;
    const matches = [...tempPara.matchAll(quoteRegex)];

    if (matches.length > 0) {
      let narrationText = tempPara;
      matches.forEach(m => {
        narrationText = narrationText.replace(m[0], "");
      });
      narrationText = narrationText.trim().replace(/^[,\s.\-–—]+|[,\s.\-–—]+$/g, "");

      const dialogueVerbs = [
        "disse", "chiese", "rispose", "esclamò", "sussurrò", "gridò", "chiamò", "ribatté", "aggiunse", "mormorò",
        "said", "asked", "replied", "whispered", "shouted", "called", "exclaimed", "yelled", "murmured"
      ];

      let speaker = null;

      for (const verb of dialogueVerbs) {
        const verbReg1 = new RegExp(`\\b${verb}\\s+(?:il|la|lo|un|una|l')?\\s*([A-ZÀ-ÿ][a-zÀ-ÿ]+)`, "i");
        const match1 = narrationText.match(verbReg1);
        if (match1 && match1[1]) {
          speaker = match1[1];
          break;
        }

        const verbReg2 = new RegExp(`\\b([A-ZÀ-ÿ][a-zÀ-ÿ]+)\\s+${verb}\\b`, "i");
        const match2 = narrationText.match(verbReg2);
        if (match2 && match2[1]) {
          speaker = match2[1];
          break;
        }
      }

      if (!speaker) {
        const colonMatch = tempPara.match(/^([A-Za-zÀ-ÿ\s]+):/);
        if (colonMatch && colonMatch[1]) {
          speaker = colonMatch[1].trim();
        }
      }

      if (!speaker && characterName && narrationText.toLowerCase().includes(characterName.toLowerCase())) {
        speaker = characterName;
      }

      const isNarrationSignificant = narrationText.length > 18 && 
        !dialogueVerbs.some(v => narrationText.toLowerCase().includes(v));

      if (isNarrationSignificant) {
        panels.push({
          type: "narrator",
          text: narrationText,
        });
      }

      matches.forEach((m) => {
        const dialogueText = m[1].trim();
        if (dialogueText) {
          const alignment = bubbleCount % 2 === 0 ? "left" : "right";
          bubbleCount++;
          panels.push({
            type: "dialogue",
            text: dialogueText,
            speaker: speaker || (alignment === "left" ? characterName || "Viaggiatore" : "PNG"),
            alignment,
          });
        }
      });
    } else {
      panels.push({
        type: "narrator",
        text: para,
      });
    }
  });

  return panels;
}

function RetryBanner({ attempt, total, lang }) {
  if (!attempt || attempt <= 0) return null;
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs sm:text-sm px-4 py-3 rounded-lg flex items-center gap-2 mb-4 animate-pulse">
      <Loader2 className="animate-spin text-amber-400 shrink-0" size={16} />
      <span>
        {t(lang, 'retry_ink_wavers')}{" "}
        <span className="opacity-65">
          ({attempt}/{total})
        </span>
      </span>
    </div>
  );
}

export default function StoryBook() {
  const { game, generateStory, makeChoice, loadGame, loading } = useGame();
  const { token, API } = useAuth();
  const [freeText, setFreeText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const inFlightRef = useRef(false);
  const webtoonContainerRef = useRef(null);

  const lang = game?.language || "en";
  const pages = game?.story_pages || [];
  const hasPages = pages.length > 0;

  const [showFleeConfirm, setShowFleeConfirm] = useState(false);
  const [fleeLoading, setFleeLoading] = useState(false);
  const [ttsPlayingIdx, setTtsPlayingIdx] = useState(null);
  const [regeneratingPanel, setRegeneratingPanel] = useState(null);

  const handleRegeneratePanel = async (pageNumber, panelIndex) => {
    if (!game?.id) return;
    const key = `${pageNumber}_${panelIndex}`;
    setRegeneratingPanel(key);
    try {
      await axios.post(
        `${API}/game/${game.id}/story/page/${pageNumber}/panel/${panelIndex}/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      toast.success(lang.startsWith("it") ? "Immagine rigenerata!" : "Image regenerated!");
      await loadGame(game.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang.startsWith("it") ? "Rigenerazione fallita" : "Regeneration failed"));
    } finally {
      setRegeneratingPanel(null);
    }
  };

  useEffect(() => {
    return () => storyTTS.stop();
  }, []);

  const handleListenPage = useCallback(async (page, idx) => {
    if (!storyTTS.isSupported() || !page?.text) return;
    if (ttsPlayingIdx === idx) {
      storyTTS.stop();
      setTtsPlayingIdx(null);
      return;
    }
    setTtsPlayingIdx(idx);
    const voiceType = storyTTS.getStoredVoiceType();
    try {
      await storyTTS.speak(sanitizeStoryText(page.text, lang), {
        lang, type: voiceType, rate: 0.95,
        onEnd: () => setTtsPlayingIdx(null),
        onError: () => setTtsPlayingIdx(null),
      });
    } catch {
      setTtsPlayingIdx(null);
    }
  }, [lang, ttsPlayingIdx]);

  // Auto-scroll logic: scroll to the loader if generating, otherwise scroll to the top of the newly generated page
  useEffect(() => {
    if (webtoonContainerRef.current) {
      const container = webtoonContainerRef.current;
      setTimeout(() => {
        if (generating) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth"
          });
        } else {
          const pageElements = container.querySelectorAll(".webtoon-page");
          if (pageElements.length > 0) {
            const lastPageElement = pageElements[pageElements.length - 1];
            container.scrollTo({
              top: lastPageElement.offsetTop,
              behavior: "smooth"
            });
          }
        }
      }, 100);
    }
  }, [pages.length, generating]);

  const [readyChoiceIds, setReadyChoiceIds] = useState([]);
  const [diceRoll, setDiceRoll] = useState(null);
  const [choicesExpanded, setChoicesExpanded] = useState(false);
  const prevPagesLengthRef = useRef(pages.length);

  useEffect(() => {
    if (pages.length > prevPagesLengthRef.current) {
      setChoicesExpanded(true);
    }
    prevPagesLengthRef.current = pages.length;
  }, [pages.length]);
  
  const lastPageNumberForBadge = pages.length > 0 ? pages[pages.length - 1]?.page_number : null;
  const lastPageChosen = pages.length > 0 ? pages[pages.length - 1]?.chosen : null;

  useEffect(() => {
    if (!game?.id || lastPageNumberForBadge == null || lastPageChosen) {
      setReadyChoiceIds([]);
      return undefined;
    }
    let alive = true;
    let consecutiveErrors = 0;
    const MAX_ERRORS = 3;
    let intervalId = null;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(
          `${API}/game/${game.id}/page/${lastPageNumberForBadge}/prefetch-status`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
        );
        if (alive) {
          const next = r.data?.ready_choice_ids || [];
          setReadyChoiceIds(prev => {
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
              return prev;
            }
            return next;
          });
        }
        consecutiveErrors = 0;
      } catch {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_ERRORS && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };
    fetchStatus();
    intervalId = setInterval(fetchStatus, 6000);
    return () => { alive = false; if (intervalId) clearInterval(intervalId); };
  }, [game?.id, lastPageNumberForBadge, lastPageChosen, API, token]);

  useEffect(() => {
    if (game?.pending_chapter_transition) {
      ambientMusic.play('chapter_transition');
    }
  }, [game?.pending_chapter_transition]);

  const handleGenerateFirst = useCallback(async () => {
    if (!game?.id) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setGenerating(true);
    setRetryAttempt(0);
    setErrorMsg(null);
    const beforeCount = (game.story_pages || []).length;
    try {
      await generateStory(game.id, (attempt) => setRetryAttempt(attempt));
      toast.success(t(lang, 'begin_adventure') + "!");
      soundManager.playNewItem();
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || e?.response?.status === 524 || e?.response?.status === 504;
      if (isTimeout) {
        toast.info(lang.startsWith("it")
          ? "La pergamena è ancora in fase di scrittura — controllo fra qualche istante..."
          : "The page is still being written — checking again in a moment...");
        let recovered = false;
        for (const wait of [4000, 6000, 8000]) {
          await new Promise(r => setTimeout(r, wait));
          try {
            await loadGame(game.id);
            const fresh = (game?.story_pages || []).length;
            if (fresh > beforeCount) {
              recovered = true;
              toast.success(lang.startsWith("it") ? "Pagina pronta!" : "Page ready!");
              soundManager.playNewItem();
              break;
            }
          } catch { /* keep polling */ }
        }
        if (!recovered) {
          const detail = aiErrorMessage(e, lang);
          setErrorMsg(detail);
          toast.error(detail);
        }
      } else {
        const detail = aiErrorMessage(e, lang);
        setErrorMsg(detail);
        toast.error(detail);
      }
    } finally {
      setGenerating(false);
      setRetryAttempt(0);
      inFlightRef.current = false;
    }
  }, [game?.id, game?.story_pages, generateStory, loadGame, lang]);

  const handleChoice = useCallback(async (choiceText, choiceId, choiceRiskInfo) => {
    if (!game?.id) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setGenerating(true);
    setRetryAttempt(0);
    setErrorMsg(null);
    const beforeCount = (game.story_pages || []).length;

    if (choiceRiskInfo && (choiceRiskInfo.risk || 0) > 0) {
      const fail_p = Math.max(0, Math.min(0.95, choiceRiskInfo.risk));
      setDiceRoll({
        pending: true,
        label: choiceRiskInfo.risk_label || (lang.startsWith("it") ? "Prova" : "Check"),
        fail_probability: fail_p,
        success_probability: 1 - fail_p,
        pct_threshold: Math.max(1, Math.min(100, Math.floor(fail_p * 100) + 1)),
      });
    }
    try {
      const result = await makeChoice(
        game.id,
        choiceText,
        choiceId,
        (attempt) => setRetryAttempt(attempt),
      );
      soundManager.playPageFlip();
      if (result?.page?.battle_started) {
        toast.info(t(lang, 'enemy_appears'));
        soundManager.playEnemyAppear();
      }
      const roll = result?.page?.risk_roll;
      if (roll) {
        setDiceRoll((prev) => ({
          ...(prev || {}),
          ...roll,
          pending: false,
        }));
      } else {
        setDiceRoll(null);
      }
      if (result?.page?.rewards?.length > 0) {
        result.page.rewards.forEach(r => {
          toast.success(`${t(lang, 'found_item')}: ${r.name} (${r.type})`);
        });
        soundManager.playNewItem();
      }
      if (result?.new_achievements?.length > 0) {
        result.new_achievements.forEach(a => {
          toast.success(t(lang, 'achievement_unlocked'));
        });
      }
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || e?.response?.status === 524 || e?.response?.status === 504;
      if (isTimeout) {
        toast.info(lang.startsWith("it")
          ? "La pergamena è ancora in fase di scrittura — controllo fra qualche istante..."
          : "The page is still being written — checking again in a moment...");
        let recovered = false;
        for (const wait of [4000, 6000, 8000]) {
          await new Promise(r => setTimeout(r, wait));
          try {
            await loadGame(game.id);
            const fresh = (game?.story_pages || []).length;
            if (fresh > beforeCount) {
              recovered = true;
              soundManager.playPageFlip();
              break;
            }
          } catch { /* keep polling */ }
        }
        if (!recovered) {
          const detail = aiErrorMessage(e, lang);
          setErrorMsg(detail);
          toast.error(detail);
        }
      } else {
        const detail = aiErrorMessage(e, lang);
        setErrorMsg(detail);
        toast.error(detail);
      }
      setDiceRoll(null);
    } finally {
      setGenerating(false);
      setRetryAttempt(0);
      inFlightRef.current = false;
    }
  }, [game?.id, game?.story_pages, makeChoice, loadGame, lang]);

  const handleFreeChoice = () => {
    if (!freeText.trim()) return;
    handleChoice(freeText.trim());
    setFreeText("");
  };

  const handleStartBattle = async () => {
    try {
      await axios.post(`${API}/game/${game.id}/battle/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadGame(game.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start battle");
    }
  };

  const handleFleeBattle = async () => {
    setFleeLoading(true);
    try {
      const r = await axios.post(`${API}/game/${game.id}/battle/flee`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowFleeConfirm(false);
      if (r.data.lost_card) {
        toast.warning(
          lang === "it" ? `Sei fuggito! Perso: ${r.data.lost_card.name}` : `You fled! Lost: ${r.data.lost_card.name}`,
          { duration: 5000 }
        );
      } else if (r.data.lost_gold > 0) {
        toast.warning(
          lang === "it" ? `Sei fuggito! Perso: ${r.data.lost_gold} oro` : `You fled! Lost: ${r.data.lost_gold} gold`,
          { duration: 5000 }
        );
      } else {
        toast.success(lang === "it" ? "Sei fuggito senza perdite" : "You escaped with no losses");
      }
      await loadGame(game.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to flee");
    } finally {
      setFleeLoading(false);
    }
  };

  // Sub-render panels of the webtoon storyboard with support for legacy pages
  const renderPagePanels = (page, pageIdx) => {
    const panels = page.panels || [];
    
    // Fallback parser for legacy saves and pages
    if (panels.length === 0) {
      const sanitizedText = sanitizeStoryText(page.text || "", lang);
      const parsed = parseStoryToPanels(sanitizedText, game?.character?.name || "");

      return (
        <div className="flex flex-col gap-4 w-full">
          {page.image_url ? (
            <div className="vignette-container vignette-wide shrink-0 animate-fadeInUp">
              <img
                src={resolveImageUrl(page.image_url, BACKEND_URL)}
                alt={`Scene Panel ${pageIdx}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          ) : (
            page.image_prompt && (
              <div className="vignette-container vignette-wide shrink-0 animate-fadeInUp">
                <div className="w-full h-full min-h-[320px] bg-gradient-to-b from-[#1a1a1d] to-[#0a0a0c] flex flex-col items-center justify-center text-[#cfb53b]/70 gap-3 p-6 text-center select-none border border-[#cfb53b]/10 rounded-md">
                  <div className="relative mb-1">
                    <BookOpen size={40} className="text-[#cfb53b]/20" />
                    <Sparkles size={14} className="absolute -top-1 -right-1 text-[#cfb53b]/50 animate-pulse" />
                  </div>
                  <span className="font-heading text-[10px] sm:text-xs uppercase tracking-wider text-[#cfb53b]/90">
                    {t(lang, 'illustration_not_available')}
                  </span>
                  <Button
                    onClick={() => handleRegeneratePanel(page.page_number, -1)}
                    disabled={regeneratingPanel === `${page.page_number}_-1`}
                    data-testid={`regenerate-legacy-${page.page_number}`}
                    className="bg-[#cfb53b]/20 hover:bg-[#cfb53b]/30 border border-[#cfb53b]/40 text-[#cfb53b] font-heading text-[10px] sm:text-xs py-1 px-3 h-auto rounded transition-all flex items-center gap-1.5"
                  >
                    {regeneratingPanel === `${page.page_number}_-1` ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        <span>{lang.startsWith("it") ? "Generazione..." : "Generating..."}</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={12} />
                        <span>{lang.startsWith("it") ? "Rigenera immagine" : "Regenerate image"}</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )
          )}
          <div className="flex flex-col gap-3">
            {parsed.map((item, pIdx) => {
              if (item.type === "narrator") {
                return (
                  <div key={pIdx} className="narrator-caption animate-fadeInUp shrink-0">
                    {item.text}
                  </div>
                );
              }
              if (item.type === "dialogue") {
                return (
                  <div
                    key={pIdx}
                    className={`flex flex-col my-1 w-full ${item.alignment === "left" ? "items-start" : "items-end"} animate-fadeInUp shrink-0`}
                  >
                    <div className={`speech-bubble speech-bubble-${item.alignment} border-2 border-[#2c1810] shadow-md w-full`}>
                      <span className="speaker-badge">{item.speaker}</span>
                      <p className="font-comic text-[12px] sm:text-[14px] leading-relaxed m-0 text-slate-800 pt-1.5 font-bold">
                        {item.text}
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }

    // Storyboard Panels layout
    return (
      <div className="flex flex-col gap-6 w-full">
        {panels.map((panel, pIdx) => {
          const layoutType = panel.layout_type || "wide";
          const dialogues = panel.dialogues || [];
          const hasNarrativeBox = !!panel.narrative_box;
          
          let layoutClass = "vignette-wide";
          if (layoutType === "portrait") layoutClass = "vignette-portrait";
          else if (layoutType === "close-up") layoutClass = "vignette-closeup";

          const tiltClass = pIdx % 4 === 1 ? "vignette-tilted-left" : pIdx % 4 === 3 ? "vignette-tilted-right" : "";

          return (
            <div
              key={pIdx}
              className="flex flex-col gap-3 w-full max-w-2xl mx-auto mb-6 shrink-0"
              data-testid={`panel-${panel.panel_number || pIdx}`}
            >
              {/* Narrative Box */}
              {hasNarrativeBox && (
                <div className="narrator-caption w-full animate-fadeInUp m-0 max-w-none">
                  {panel.narrative_box}
                </div>
              )}

              {/* The Panel Image */}
              <div className={`vignette-container ${layoutClass} ${tiltClass} shrink-0 w-full`}>
                {panel.image_url ? (
                  <img
                    src={resolveImageUrl(panel.image_url, BACKEND_URL)}
                    alt={`Panel ${panel.panel_number}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full min-h-[320px] bg-gradient-to-b from-[#1a1a1d] to-[#0a0a0c] flex flex-col items-center justify-center text-[#cfb53b]/70 gap-3 p-6 text-center select-none border border-[#cfb53b]/10">
                    <div className="relative mb-1">
                      <BookOpen size={40} className="text-[#cfb53b]/20" />
                      <Sparkles size={14} className="absolute -top-1 -right-1 text-[#cfb53b]/50 animate-pulse" />
                    </div>
                    <span className="font-heading text-[10px] sm:text-xs uppercase tracking-wider text-[#cfb53b]/90">
                      {t(lang, 'illustration_not_available')}
                    </span>
                    <span className="font-body text-[9px] sm:text-[11px] text-[#94a3b8] max-w-[220px] mb-1">
                      {t(lang, 'chronicle_continues')}
                    </span>
                    <Button
                      onClick={() => handleRegeneratePanel(page.page_number, pIdx)}
                      disabled={regeneratingPanel === `${page.page_number}_${pIdx}`}
                      data-testid={`regenerate-panel-${page.page_number}-${pIdx}`}
                      className="bg-[#cfb53b]/20 hover:bg-[#cfb53b]/30 border border-[#cfb53b]/40 text-[#cfb53b] font-heading text-[10px] sm:text-xs py-1 px-3 h-auto rounded transition-all flex items-center gap-1.5"
                    >
                      {regeneratingPanel === `${page.page_number}_${pIdx}` ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          <span>{lang.startsWith("it") ? "Generazione..." : "Generating..."}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={12} />
                          <span>{lang.startsWith("it") ? "Rigenera immagine" : "Regenerate image"}</span>
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Dialogues rendered outside below the illustration */}
              {dialogues.length > 0 && (
                <div className="flex flex-col gap-2 w-full px-2 sm:px-4 mt-1">
                  {dialogues.map((dialogue, dIdx) => {
                    const alignment = dIdx % 2 === 0 ? "left" : "right";
                    return (
                      <div
                        key={dIdx}
                        className={`flex flex-col w-full ${alignment === "left" ? "items-start" : "items-end"} animate-fadeInUp`}
                      >
                        <div className={`speech-bubble speech-bubble-${alignment} border-2 border-[#2c1810] shadow-md w-full max-w-[85%]`}>
                          <span className="speaker-badge">{dialogue.character || (alignment === "left" ? game?.character?.name || "Protagonista" : "PNG")}</span>
                          <p className="font-comic text-[12px] sm:text-[14px] leading-relaxed m-0 text-slate-800 pt-1.5 font-bold">
                            {dialogue.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!hasPages) {
    return (
      <div className="flex-1 flex items-center justify-center py-16" data-testid="story-empty">
        <RetryBanner attempt={retryAttempt} total={2} lang={lang} />
        <div className="text-center max-w-md mx-auto px-4">
          <div className="relative mb-8">
            <BookOpen size={72} className="mx-auto text-[#cfb53b]/30" />
            <div className="absolute inset-0 bg-[#cfb53b]/5 rounded-full blur-[30px]" />
          </div>
          <h2 className="font-heading text-3xl text-[#cfb53b] mb-4">{t(lang, 'book_awaits')}</h2>
          <p className="text-[#94a3b8] mb-3 font-body leading-relaxed">
            {t(lang, 'story_not_written')}{" "}
            <span className="text-[#cfb53b] font-semibold">{game?.character?.name}</span> {t(lang, 'in_world')}{" "}
            <span className="text-[#cfb53b] font-semibold capitalize">{game?.setting?.replace(/_/g, " ")}</span>.
          </p>
          <p className="text-[#64748b] text-sm mb-8 font-body">
            {t(lang, 'starting_items')}: {game?.inventory?.objects?.map(o => o.name).join(", ") || "None"} | 
            {t(lang, 'abilities')}: {game?.inventory?.abilities?.map(a => a.name).join(", ") || "None"}
          </p>

          {errorMsg && (
            <div className="mb-4 p-3 bg-[#8b0000]/10 border border-[#8b0000]/30 rounded-lg text-sm text-red-300 flex items-start gap-2" data-testid="error-message">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <Button
            data-testid="begin-story-button"
            onClick={handleGenerateFirst}
            disabled={generating}
            className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold text-lg px-12 py-4 animate-pulse-glow rounded-md"
          >
            {generating ? (
              <span className="flex items-center gap-3"><Loader2 className="animate-spin" size={22} /> {t(lang, 'writing_fate')}</span>
            ) : t(lang, 'open_book')}
          </Button>
        </div>
      </div>
    );
  }

  const lastPage = pages[pages.length - 1];
  const battlePending = game?.battle_state?.pending && !game?.battle_state?.active;
  const pendingAftermath = game?.pending_battle_aftermath;
  const pendingTransition = game?.pending_chapter_transition;
  const aftermathMode = !!(pendingAftermath || pendingTransition);
  const showChoices = lastPage && !lastPage.chosen && !generating && !battlePending && !aftermathMode;

  return (
    <div className="py-2 sm:py-6 px-1 sm:px-4" data-testid="story-book">
      {diceRoll && (
        <DiceRollModal
          roll={diceRoll}
          lang={lang}
          onClose={() => {
            const wasPending = diceRoll.pending;
            setDiceRoll(null);
            if (wasPending && game?.id) {
              loadGame(game.id).catch(() => {});
            }
          }}
        />
      )}
      <RetryBanner attempt={retryAttempt} total={2} lang={lang} />
      
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Missing Images Banner */}
        <MissingImagesPanel variant="banner" />

        {/* Webtoon Strip View */}
        <div 
          ref={webtoonContainerRef}
          className="webtoon-strip-container overflow-y-auto max-h-[68vh] sm:max-h-[72vh] p-2 sm:p-5 bg-[#141417]/95 border border-[#cfb53b]/15 rounded-lg mb-4 shadow-[0_10px_50px_rgba(0,0,0,0.65)] relative"
          style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
          data-testid="story-webtoon-strip"
        >
          <div className="flex flex-col gap-8 max-w-2xl mx-auto w-full pb-8">
            {pages.map((page, idx) => {
              const pageChapter = page.chapter || Math.ceil((idx + 1) / 10);
              const isChapterStart = page.is_chapter_start || idx === 0;

              return (
                <div key={idx} className="webtoon-page flex flex-col gap-4 font-comic w-full">
                  {/* Chapter Header */}
                  {isChapterStart && (
                    <div className="text-center py-2 bg-gradient-to-r from-transparent via-[#8b7355]/15 to-transparent border-y border-[#8b7355]/40 shrink-0">
                      <span className="font-heading text-xs sm:text-sm text-[#cfb53b] tracking-[0.3em] uppercase" data-testid={`chapter-banner-${pageChapter}`}>
                        {tf(lang, 'chapter_num', [pageChapter])}
                      </span>
                      {page.chapter_bridge && (page.chapter_bridge.ending_title_it || page.chapter_bridge.ending_title_en) && (
                        <div
                          data-testid={`chapter-ending-title-${page.chapter_bridge.from_chapter}`}
                          className="mt-1 text-[10px] sm:text-xs text-[#8b7355] italic font-body tracking-wider"
                        >
                          {lang.startsWith("it") ? "Precedentemente: " : "Previously: "}
                          <span className="text-[#cfb53b]">
                            {lang.startsWith("it") ? page.chapter_bridge.ending_title_it : page.chapter_bridge.ending_title_en}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 border-b border-[#cfb53b]/10 pb-1 shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-xs text-[#cfb53b] tracking-[0.1em] uppercase">
                        {t(lang, 'chapter')} {pageChapter}
                      </span>
                      <WorldClockBadge />
                    </div>

                    {/* TTS Voice Control */}
                    {storyTTS.isSupported() && page.text && (
                      <button
                        onClick={() => handleListenPage(page, idx)}
                        data-testid={`tts-listen-${idx}`}
                        className={`inline-flex items-center gap-1 text-[10px] font-heading rounded-full px-2 py-0.5 border transition-all ${
                          ttsPlayingIdx === idx
                            ? "bg-[#7a1f1f] text-[#f5e6c8] border-[#7a1f1f]"
                            : "bg-[#cfb53b]/10 text-[#cfb53b] border-[#8b7355]/30 hover:bg-[#cfb53b]/20"
                        }`}
                        title={lang.startsWith("it") ? "Ascolta" : "Listen"}
                      >
                        {ttsPlayingIdx === idx ? <StopCircle size={10} /> : <Headphones size={10} />}
                        <span className="text-[9px] uppercase">{ttsPlayingIdx === idx ? (lang.startsWith("it") ? "Ferma" : "Stop") : (lang.startsWith("it") ? "Ascolta" : "Listen")}</span>
                      </button>
                    )}
                  </div>

                  {/* Render Story Panels or Legacy Fallback */}
                  {renderPagePanels(page, idx)}

                  {/* Page Rewards */}
                  {page.rewards && page.rewards.length > 0 && (
                    <div className="mt-2 p-2 bg-[#cfb53b]/10 rounded border border-[#cfb53b]/30 shrink-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Gift size={11} className="text-[#cfb53b]" />
                        <span className="text-[10px] font-heading text-[#cfb53b] uppercase tracking-wider">{t(lang, 'rewards_found')}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {page.rewards.map((r, ri) => (
                          <span key={ri} className="text-[10px] text-[#f5e6c8] bg-[#cfb53b]/20 px-1.5 py-0.5 rounded border border-[#cfb53b]/30 font-medium">{r.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Page Battle Alert */}
                  {page.encounter && (
                    <div className={`mt-1 p-2 rounded border shrink-0 ${page.encounter.is_boss ? 'bg-[#7a1f1f]/10 border-[#7a1f1f]/50 animate-pulse' : 'bg-[#8b0000]/10 border-[#8b0000]/30'}`}>
                      <div className="flex items-center gap-1.5">
                        {page.encounter.is_boss ? <Crown size={12} className="text-[#7a1f1f]" /> : <Swords size={11} className="text-[#8b0000]" />}
                        <span className={`text-[10px] font-heading uppercase tracking-wider ${page.encounter.is_boss ? 'text-[#7a1f1f] font-bold' : 'text-[#8b0000]'}`}>
                          {page.encounter.is_boss ? t(lang, 'boss_battle') : t(lang, 'battle_alert')}: {page.encounter.name}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Choice selected on this page */}
                  {page.chosen && (
                    <div className="mt-2 pt-2 border-t border-[#cfb53b]/20 shrink-0">
                      <span className="text-[11px] text-[#e2e8f0]/80 font-story">{t(lang, 'your_choice')}: <span className="text-[#cfb53b] italic font-semibold">"{page.chosen}"</span></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Global error block */}
        {errorMsg && (
          <div className="mb-4 p-4 bg-[#8b0000]/10 border border-[#8b0000]/30 rounded-lg text-sm text-red-300 flex items-start gap-3 animate-fadeInUp" data-testid="error-message">
            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-red-400" />
            <div>
              <p className="font-heading text-sm text-red-400 mb-1">{t(lang, 'generation_error')}</p>
              <p>{errorMsg}</p>
            </div>
          </div>
        )}

        {/* Battle pending control panel */}
        {battlePending && game?.battle_state && (
          <div className="bg-gradient-to-br from-[#8b0000]/20 to-[#1a1a1d]/90 backdrop-blur-sm border-2 border-[#cfb53b]/40 rounded-lg p-4 sm:p-6 mb-16 sm:mb-4 animate-fadeInUp" data-testid="battle-pending-panel">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#8b0000]/30 border border-[#cfb53b]/50 flex items-center justify-center shrink-0 animate-pulse">
                <Swords size={18} className="text-[#cfb53b]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm sm:text-base text-[#cfb53b]">
                  {game.battle_state.enemy?.is_boss ? t(lang, 'boss_battle') : t(lang, 'battle_alert')}: <span className="text-[#e2e8f0]">{game.battle_state.enemy?.name}</span>
                </p>
                <p className="text-[11px] sm:text-xs text-[#94a3b8] mt-0.5">{t(lang, 'battle_encounter_hint')}</p>
              </div>
            </div>
            <div className={`grid gap-2 ${game.battle_state.enemy?.is_boss ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-[1fr_auto]'}`}>
              <Button
                onClick={handleStartBattle}
                disabled={fleeLoading}
                className="w-full bg-gradient-to-r from-[#8b0000] to-[#cfb53b] hover:from-[#a00000] hover:to-[#d4af37] text-[#0a0a0c] font-heading text-sm sm:text-base py-5 sm:py-6 shadow-lg hover:shadow-[0_0_20px_rgba(207,181,59,0.5)] transition-all"
                data-testid="start-battle-btn"
              >
                <Swords size={18} className="mr-2" /> {t(lang, 'start_battle')}
              </Button>
              {!game.battle_state.enemy?.is_boss && (
                <Button
                  onClick={() => setShowFleeConfirm(true)}
                  disabled={fleeLoading}
                  variant="outline"
                  className="w-full sm:w-auto border-[#cfb53b]/40 text-[#cfb53b] hover:bg-[#cfb53b]/10 hover:border-[#cfb53b]/70 hover:text-[#cfb53b] font-heading text-sm sm:text-base py-5 sm:py-6 px-4 sm:px-6 transition-all"
                  data-testid="flee-battle-btn"
                >
                  {fleeLoading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <Footprints size={18} className="mr-2" /> {t(lang, 'flee_battle')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        <AlertDialog open={showFleeConfirm} onOpenChange={(v) => !fleeLoading && setShowFleeConfirm(v)}>
          <AlertDialogContent className="bg-[#1a1a1d] border-[#cfb53b]/30 text-[#e2e8f0]" data-testid="flee-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-[#cfb53b] flex items-center gap-2">
                <Swords size={20} /> {t(lang, 'flee_confirm_title')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#94a3b8] font-body leading-relaxed">
                {t(lang, 'flee_confirm_desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={fleeLoading}
                className="bg-transparent border-[#cfb53b]/30 text-[#e2e8f0] hover:bg-[#cfb53b]/10 hover:text-[#cfb53b]"
                data-testid="flee-cancel-btn"
              >
                {t(lang, 'flee_cancel_btn')}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={fleeLoading}
                onClick={(e) => { e.preventDefault(); handleFleeBattle(); }}
                className="bg-[#8b0000] hover:bg-[#a00000] text-[#e2e8f0] font-heading"
                data-testid="flee-confirm-btn"
              >
                {fleeLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {t(lang, 'flee_confirm_btn')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Aftermath or Chapter transition action button */}
        {aftermathMode && !generating && (
          <div className="bg-gradient-to-br from-[#cfb53b]/15 to-[#1a1a1d]/90 backdrop-blur-sm border-2 border-[#cfb53b]/30 rounded-lg p-4 sm:p-6 mb-16 sm:mb-4 animate-fadeInUp" data-testid="aftermath-panel">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#cfb53b]/20 border border-[#cfb53b]/50 flex items-center justify-center shrink-0">
                <BookOpen size={18} className="text-[#cfb53b]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm sm:text-base text-[#cfb53b]">
                  {pendingTransition
                    ? (lang.startsWith("it") ? "Fine del Capitolo" : "End of Chapter")
                    : pendingAftermath?.outcome === "victory"
                      ? (lang.startsWith("it") ? "Vittoria" : "Victory")
                      : (lang.startsWith("it") ? "Sconfitta" : "Defeat")}
                </p>
                <p className="text-[11px] sm:text-xs text-[#94a3b8] mt-0.5">
                  {pendingTransition
                    ? (lang.startsWith("it")
                        ? `Hai chiuso il Capitolo ${pendingTransition.previous_chapter}. Il mondo ti attende oltre...`
                        : `Chapter ${pendingTransition.previous_chapter} complete. A new world awaits...`)
                    : pendingAftermath?.outcome === "victory"
                      ? (lang.startsWith("it")
                          ? `Hai sconfitto ${pendingAftermath.enemy_name}.`
                          : `You have defeated ${pendingAftermath.enemy_name}.`)
                      : (lang.startsWith("it")
                          ? `Sei stato sconfitto da ${pendingAftermath.enemy_name}...`
                          : `You were defeated by ${pendingAftermath.enemy_name}...`)}
                </p>
              </div>
            </div>
            <Button
              data-testid="continue-story-btn"
              onClick={handleGenerateFirst}
              disabled={generating}
              className="w-full bg-gradient-to-r from-[#cfb53b] to-[#d4af37] hover:from-[#d4af37] hover:to-[#e6c43a] text-[#0a0a0c] font-heading text-sm sm:text-base py-5 sm:py-6 shadow-lg hover:shadow-[0_0_20px_rgba(207,181,59,0.5)] transition-all"
            >
              {generating ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <ChevronRight size={18} className="mr-2" />
              )}
              {pendingTransition
                ? (lang.startsWith("it") ? "Passa al prossimo Capitolo" : "Proceed to next Chapter")
                : (lang.startsWith("it") ? "Continua la storia" : "Continue the story")}
            </Button>
          </div>
        )}

        {/* Choices selection panel (active choices on latest page) */}
        {showChoices && (
          <div className="bg-[#1a1a1d]/80 backdrop-blur-sm border border-[#cfb53b]/15 rounded-lg p-3 sm:p-6 mb-16 sm:mb-4 animate-fadeInUp transition-all" data-testid="choices-panel">
            <h3 
              onClick={() => setChoicesExpanded(!choicesExpanded)}
              className="font-heading text-sm sm:text-lg text-[#cfb53b] flex items-center justify-between w-full cursor-pointer select-none py-1 group"
              data-testid="choices-panel-header"
            >
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 bg-[#cfb53b] rounded-full animate-pulse" />
                <span>{t(lang, 'what_do_you_do')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#cfb53b]/60 group-hover:text-[#cfb53b] transition-colors">
                <span>
                  {choicesExpanded 
                    ? (lang.startsWith("it") ? "Nascondi" : "Hide") 
                    : (lang.startsWith("it") ? "Mostra" : "Show")}
                </span>
                {choicesExpanded ? (
                  <ChevronUp size={16} className="text-[#cfb53b]" />
                ) : (
                  <ChevronDown size={16} className="text-[#cfb53b]" />
                )}
              </div>
            </h3>
            
            {choicesExpanded && (
              <div className="mt-3 sm:mt-5 animate-fadeIn">
                <div className="grid gap-2 sm:gap-3 mb-3 sm:mb-5 stagger-children">
                  {lastPage.choices?.map((choice, idx) => {
                    const risk = Number(choice.risk || 0);
                    const hasRisk = risk > 0;
                    const successPct = hasRisk ? Math.round((1 - risk) * 100) : null;
                    const isReady = !hasRisk && choice.id && readyChoiceIds.includes(String(choice.id));
                    const riskClass = !hasRisk ? "" :
                      risk >= 0.6 ? "bg-red-500/15 text-red-300 border-red-500/30" :
                      risk >= 0.4 ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                                    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
                    return (
                      <Button
                        key={choice.id || idx}
                        data-testid={`choice-${choice.id || idx}`}
                        onClick={() => handleChoice(choice.text, choice.id, hasRisk ? { risk, risk_label: choice.risk_label } : null)}
                        disabled={generating}
                        variant="outline"
                        className={`w-full text-left justify-start py-2.5 sm:py-4 px-2.5 sm:px-5 text-[#e2e8f0] transition-all animate-fadeInUp font-body group h-auto whitespace-normal ${
                          hasRisk
                            ? "border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/50"
                            : isReady
                            ? "border-emerald-400/40 hover:bg-emerald-400/8 hover:border-emerald-400/60 hover:text-emerald-200 shadow-[0_0_12px_rgba(110,231,183,0.12)]"
                            : "border-[#cfb53b]/15 hover:bg-[#cfb53b]/8 hover:border-[#cfb53b]/30 hover:text-[#cfb53b]"
                        }`}
                      >
                        <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#cfb53b]/10 text-[#cfb53b] font-heading text-[10px] sm:text-xs mr-2 sm:mr-3 shrink-0 group-hover:bg-[#cfb53b]/20 transition-colors">
                          {idx + 1}
                        </span>
                        <span className="flex-1 leading-snug text-xs sm:text-sm break-words">
                          {choice.text}
                        </span>
                        {isReady && (
                          <span
                            data-testid={`choice-ready-${choice.id || idx}`}
                            className="ml-2 shrink-0 inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-heading px-1.5 py-0.5 rounded border bg-emerald-500/12 text-emerald-300 border-emerald-400/30 animate-fadeInUp"
                            title={lang === 'it' ? 'Pagina già pronta — clic istantaneo ✨' : 'Page already prepared — instant click ✨'}
                          >
                            <span aria-hidden="true">✨</span>
                            <span>{lang === 'it' ? 'Pronta' : 'Ready'}</span>
                          </span>
                        )}
                        {hasRisk && (
                          <span
                            data-testid={`choice-risk-${choice.id || idx}`}
                            className={`ml-2 shrink-0 text-[9px] sm:text-[10px] font-heading px-1.5 py-0.5 rounded border ${riskClass}`}
                            title={`${choice.risk_label || "Check"} — ${successPct}% success`}
                          >
                            {choice.risk_label ? `${choice.risk_label} ` : ""}🎲 {successPct}%
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>

                {/* Custom choice text input */}
                <div className="pt-3 sm:pt-4 border-t border-[#2d3748]">
                  <p className="text-[10px] sm:text-xs text-[#64748b] mb-1.5 sm:mb-2 font-heading">{t(lang, 'forge_own_path')}</p>
                  <div className="flex gap-2 sm:gap-3">
                    <Input
                      data-testid="free-choice-input"
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleFreeChoice()}
                      placeholder={t(lang, 'describe_action')}
                      className="flex-1 bg-[#0a0a0c] border-[#cfb53b]/15 text-[#e2e8f0] placeholder:text-[#4a5568] focus:border-[#cfb53b]/40"
                    />
                    <Button
                      data-testid="free-choice-submit"
                      onClick={handleFreeChoice}
                      disabled={!freeText.trim() || generating}
                      className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] px-4"
                    >
                      <Send size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading fate state */}
        {generating && (
          <div className="text-center py-10 animate-fadeInUp" data-testid="generating-indicator">
            <div className="relative inline-block">
              <Loader2 className="animate-spin text-[#cfb53b]" size={36} />
              <div className="absolute inset-0 bg-[#cfb53b]/10 rounded-full blur-[20px]" />
            </div>
            <p className="font-heading text-[#cfb53b] mt-4 text-lg">{t(lang, 'writing_fate')}</p>
            <p className="text-[#64748b] text-sm mt-1">{t(lang, 'generating_story')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
